import React, { useState } from 'react';

import { Button, Checkbox } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { useIntl } from 'react-intl';
import { IChallengeCheckoutPaymentProduct } from 'src/shared/models';
import { capitalizeFirstLetter, checkUserResourcePoints } from 'utils';
import { ChallengeTypeEnum } from 'src/shared/enums';

interface OwnProps {
  buttonDisabled: boolean;
  challengeType: ChallengeTypeEnum;
  checkoutName: string;
  loading: boolean;
  performCheckout: () => void;
  product?: IChallengeCheckoutPaymentProduct;
  singleProduct: boolean;
  termsLabel: string;
  termsUrl: string;
  userPoints: number;
  taxLabel: number;
}

export default function CheckoutPaymentDetails({
  buttonDisabled,
  challengeType,
  checkoutName,
  loading,
  performCheckout,
  product,
  singleProduct,
  termsLabel,
  termsUrl,
  userPoints,
  taxLabel,
}: OwnProps) {
  const { formatMessage } = useIntl();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const hasUserEnoughPoints = checkUserResourcePoints(
    userPoints,
    product?.score,
    challengeType,
  );
  const pointsLabel = Math.abs(product?.score);
  const moneyLabel = Math.abs(product?.price);

  const handleCheck = (ev: CheckboxChangeEvent) => {
    setTermsAccepted(ev.target.checked);
  };

  const getTitle = () => {
    if (singleProduct) return product.lead;
    return `${capitalizeFirstLetter(checkoutName)} ${product.lead}`;
  };

  return (
    <div className="checkout-payment-product-details">
      {product && (
        <>
          <div className="payment-title-command">
            {formatMessage({ id: 'page.experience.checkout.summary-label' })}
          </div>
          <div className="checkout-payment-product">
            <div className="checkout-payment-product__header">
              <div className="checkout-payment-product__header--image">
                <img alt="checkout-img" src={product.img} />
              </div>
              <span className="checkout-payment-product__header--product-name">
                {getTitle()}
              </span>
            </div>
            <div className="checkout-payment-product__description">
              <div className="checkout-payment-product__description--content">
                {product.description}
              </div>
            </div>
          </div>

          <div className="checkout-payment-product__product-wrapper">
            <div className="payment-subtotal">
              <span>
                {formatMessage({
                  id: 'page.experience.checkout.subtotal-points',
                })}
              </span>
              <span className="payment-subtotal-amount">
                {formatMessage(
                  { id: 'page.experience.checkout.subtotal-points.points' },
                  { points: pointsLabel },
                )}
              </span>
            </div>
            <div className="payment-subtotal">
              <span>
                {formatMessage({
                  id: 'page.experience.checkout.subtotal-money',
                })}
              </span>
              <span className="payment-subtotal-amount">
                {formatMessage(
                  { id: 'page.experience.checkout.summary-points.euros' },
                  { moneyAmount: moneyLabel },
                )}
              </span>
            </div>
            <div className="payment-subtotal">
              <span>
                {formatMessage({
                  id: 'page.experience.checkout.iva',
                })}
              </span>
              <span className="payment-subtotal-amount">
                {formatMessage(
                  { id: 'page.experience.checkout.summary-points.euros' },
                  { moneyAmount: taxLabel },
                )}
              </span>
            </div>
            <div className="payment-shipment">
              <div className="payment-shipment-days">
                {formatMessage({ id: 'page.experience.checkout.shipment' })}
              </div>
              <div className="payment-shipment-amount">
                {formatMessage({
                  id: 'page.experience.checkout.summary-points.free',
                })}
              </div>
            </div>
            <div className="payment-shipment">
              <div className="payment-shipment-regions">
                {formatMessage({
                  id: 'page.experience.checkout.shipment-exception',
                })}
              </div>
            </div>

            <div className="payment-total">
              <span>
                {formatMessage({
                  id: 'page.experience.checkout.total',
                })}
              </span>
              <span className="payment-total-money-points">
                {formatMessage(
                  {
                    id: 'page.experience.checkout.summary-points.points-euros',
                  },
                  { points: pointsLabel, moneyAmount: moneyLabel },
                )}
              </span>
            </div>
          </div>

          <div className="checkout-payment-btn__wrapper">
            <div className="checkout-payment-product__terms">
              <Checkbox
                checked={termsAccepted}
                className="checkbox-remember-login checkout-product__checkbox"
                onChange={handleCheck}>
                <span className="checkout-payment-product__terms--content">
                  {formatMessage({
                    id: 'page.experience.checkout.summary-terms-payment',
                  })}
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href={termsUrl}
                    className="link underline-text checkout-payment-product__terms--link_conditions">
                    {termsLabel}
                  </a>
                </span>
              </Checkbox>
            </div>
            {
              <Button
                id="checkout-payment-modal-send"
                className="btn-primary btn-primary--black btn-primary__small checkout-modal-send-payment"
                disabled={
                  buttonDisabled || !termsAccepted || !hasUserEnoughPoints
                }
                loading={loading}
                onClick={performCheckout}>
                {formatMessage({ id: 'page.experience.checkout.go-payment' })}
                <div className="icon icon--external_link icon-go-payment" />
              </Button>
            }
          </div>
        </>
      )}
    </div>
  );
}
